import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  Moon, Upload, LoaderCircle, Check, TriangleAlert, ShieldCheck, Download, RotateCcw, Database, Store,
  CircleCheckBig, CircleX, RefreshCw, HardDrive, Info, Image as ImageIcon,
} from 'lucide-react';
import api from '../../api/axios';
import SearchSelect from '../../components/SearchSelect';

// Baza Restore → serverdəki backup-lardan bərpa:
//  (FiscalSTOK-dan köçürülüb)
//  • Mənbə: gecə/«İndi al» backup-ları (bütün tenantlar) və ya kənardan yüklənən .sql(.gz) + şəkil .tar.gz
//  • Tək tenant: dump-dan YALNIZ seçilən tenantın sətirləri (başqalarına toxunulmur)
//  • Bütün baza: hamısı backup gününə qayıdır (əvvəl hazırkı baza avtomatik nüsxələnir)
//  • Təhlükəsizlik nüsxələri: hər silmə/bərpadan əvvəl avtomatik alınan nüsxələr
const CARD = 'rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]';
const FULL_CONFIRM = 'HAMISINI BƏRPA ET';
const fmtSize = b => (b == null ? '-' : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const CHUNK = 2 * 1024 * 1024; // böyük faylı 2MB-lıq hissələrlə yüklə (nginx limitinə ilişmir)
const fmtTime = d => new Date(d).toLocaleString('az-AZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function RestoreCenter({ tenants = [], onRestored }) {
  const [src,      setSrc]      = useState(null);   // { kind:'night'|'upload', name, img? , label }
  const [sources,  setSources]  = useState(null);
  const [srcTab,   setSrcTab]   = useState('night');
  const [mode,     setMode]     = useState('tenant'); // tenant | full
  const [info,     setInfo]     = useState(null);   // dump inspect
  const [inspBusy, setInspBusy] = useState(false);
  const [tid,      setTid]      = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [withImg,  setWithImg]  = useState(true);
  const [busy,     setBusy]     = useState(false);
  const [done,     setDone]     = useState(null);
  const [upBusy,   setUpBusy]   = useState(false);
  const [full,     setFull]     = useState(null);
  const sqlRef = useRef(null), imgRef = useRef(null);
  const [upSql, setUpSql] = useState(null);
  const [upImg, setUpImg] = useState(null);

  const loadSources = useCallback(async () => {
    try {
      const { data } = await api.get('/superadmin/restore/sources');
      if (data.ok) { setSources(data.data); setFull(data.data.full); }
    } catch { /* sakit */ }
  }, []);
  useEffect(() => { loadSources(); }, [loadSources]);

  // Bütün baza bərpası gedirsə statusu izlə (proqram sonda yenidən başlayır, qısa kəsinti normaldır)
  useEffect(() => {
    if (!full || full.state !== 'RUNNING') return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/superadmin/restore/full/status');
        if (data.ok) {
          setFull(data.data);
          if (data.data && data.data.state === 'OK') { toast.success('Bütün baza bərpa olundu', { duration: 8000 }); onRestored && onRestored(); loadSources(); }
          if (data.data && data.data.state === 'FAIL') { toast.error(data.data.msg || 'Bərpa alınmadı', { duration: 10000 }); loadSources(); }
        }
      } catch { setFull(f => ({ ...f })); } // proqram yenidən başlayır → bir az sonra yenə yoxla
    }, 3000);
    return () => clearTimeout(t);
  }, [full, loadSources, onRestored]);

  async function pickSource(s) {
    setSrc(s); setInfo(null); setTid(''); setConfirm(''); setDone(null);
    setInspBusy(true);
    try {
      const { data } = await api.post('/superadmin/restore/dump/inspect', { source: s });
      if (!data.ok) { toast.error(data.msg || 'Fayl oxunmadı'); setSrc(null); return; }
      setInfo(data.data);
    } catch { toast.error('Fayl oxunmadı'); setSrc(null); }
    finally { setInspBusy(false); }
  }

  async function pickTenant(id) {
    setTid(id); setConfirm(''); setDone(null);
    if (!id) return;
    setInspBusy(true);
    try {
      const { data } = await api.post('/superadmin/restore/dump/inspect', { source: src, tenant_id: parseInt(id, 10) });
      if (!data.ok) { toast.error(data.msg || 'Oxunmadı'); return; }
      setInfo(data.data);
    } catch { toast.error('Oxunmadı'); }
    finally { setInspBusy(false); }
  }

  const [upPct, setUpPct] = useState(null);
  // Bir faylı hissə-hissə yüklə → serverdə yoxlanılır (gzip, FiscalPOS dump-ı) və adlandırılır
  async function uploadOne(file, label) {
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append('chunk', file.slice(i * CHUNK, Math.min((i + 1) * CHUNK, file.size)), `c-${i}`);
      fd.append('uploadId', id);
      fd.append('chunkIndex', String(i));
      const { data } = await api.post('/superadmin/restore/upload-chunk', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      if (!data.ok) throw new Error(data.msg || 'Hissə yüklənmədi');
      setUpPct({ label, pct: Math.round(((i + 1) / total) * 100) });
    }
    setUpPct({ label, pct: 100, checking: true });
    const { data } = await api.post('/superadmin/restore/upload-finalize', { uploadId: id, filename: file.name }, { timeout: 0 });
    if (!data.ok) throw new Error(data.msg || 'Fayl qəbul olunmadı');
    return data.data.name;
  }

  async function doUpload() {
    if (!upSql && !upImg) return;
    setUpBusy(true);
    try {
      const sqlName = upSql ? await uploadOne(upSql, 'Baza') : null;
      const imgName = upImg ? await uploadOne(upImg, 'Şəkillər') : null;
      toast.success('Fayl serverə yükləndi');
      setUpSql(null); setUpImg(null);
      await loadSources();
      if (sqlName) pickSource({ kind: 'upload', name: sqlName, img: imgName || undefined, label: sqlName });
    } catch (e) { toast.error(e?.response?.data?.msg || e.message || 'Yüklənmədi', { duration: 8000 }); }
    finally { setUpBusy(false); setUpPct(null); }
  }

  const detail = info && info.detail;
  const curName = detail && detail.current ? String(detail.current.name || '').trim() : '';
  const nameOk = !!curName && confirm.trim() === curName;

  async function doTenantRestore() {
    if (!nameOk) return;
    setBusy(true); setDone(null);
    try {
      const { data } = await api.post('/superadmin/restore/dump/tenant', { source: src, tenant_id: parseInt(tid, 10), confirmName: confirm.trim(), withImages: withImg }, { timeout: 0 });
      if (!data.ok) { toast.error(data.msg || 'Bərpa alınmadı', { duration: 10000 }); return; }
      setDone({ kind: 'tenant', ...data.data });
      setConfirm('');
      toast.success('Tenant bərpa olundu', { duration: 6000 });
      onRestored && onRestored();
      loadSources();
    } catch (e) { toast.error(e?.response?.data?.msg || 'Bərpa alınmadı, heç nə dəyişmədi', { duration: 10000 }); }
    finally { setBusy(false); }
  }

  async function doFullRestore() {
    if (confirm.trim() !== FULL_CONFIRM) return;
    setBusy(true);
    try {
      const { data } = await api.post('/superadmin/restore/full', { source: src, confirmText: confirm.trim(), withImages: withImg });
      if (!data.ok) { toast.error(data.msg || 'Başladılmadı', { duration: 8000 }); return; }
      setConfirm('');
      setFull({ state: 'RUNNING', msg: 'başladılır', at: '' });
      toast('Bütün bazanın bərpası başladı', { icon: '⏳' });
    } catch { toast.error('Başladılmadı'); }
    finally { setBusy(false); }
  }

  async function safetyDownload(file) {
    try {
      const res = await api.get(`/superadmin/restore/safety/download?file=${encodeURIComponent(file)}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = file; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error('Endirilmədi'); }
  }
  const [safeTarget, setSafeTarget] = useState(null);   // { file, tenant }
  const [safeConfirm, setSafeConfirm] = useState('');
  async function safetyApply() {
    if (!safeTarget) return;
    setBusy(true);
    try {
      const { data } = await api.post('/superadmin/restore/safety/apply', { file: safeTarget.file, confirmName: safeConfirm.trim() }, { timeout: 0 });
      if (!data.ok) { toast.error(data.msg || 'Bərpa alınmadı', { duration: 10000 }); return; }
      toast.success('Nüsxədən bərpa olundu', { duration: 6000 });
      setSafeTarget(null); setSafeConfirm('');
      onRestored && onRestored();
      loadSources();
    } catch (e) { toast.error(e?.response?.data?.msg || 'Bərpa alınmadı'); }
    finally { setBusy(false); }
  }
  const tenantOfFile = f => { const m = f.match(/^tenant(\d+)_/); return m ? tenants.find(t => t.id === parseInt(m[1], 10)) : null; };

  const fullRunning = full && full.state === 'RUNNING';

  return (
    <div className="space-y-5">
      {/* ── Bütün baza bərpası gedirsə ── */}
      {full && (fullRunning || full.at) && (
        <div className={`flex items-start gap-3 rounded-2xl px-4 py-3 ring-1 ${fullRunning ? 'bg-sky-50 ring-sky-200' : full.state === 'OK' ? 'bg-emerald-50 ring-emerald-200' : 'bg-red-50 ring-red-200'}`}>
          {fullRunning ? <LoaderCircle className="mt-0.5 h-5 w-5 flex-shrink-0 animate-spin text-[#0090bb]" /> : full.state === 'OK' ? <CircleCheckBig className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" /> : <CircleX className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />}
          <div className="min-w-0 text-sm">
            <p className="font-semibold text-slate-900">{fullRunning ? 'Bütün baza bərpa olunur' : full.state === 'OK' ? 'Son bütün baza bərpası' : 'Son bütün baza bərpası alınmadı'}</p>
            <p className="text-xs text-slate-600">{full.msg}{full.at ? ` · ${full.at}` : ''}</p>
            {fullRunning && <p className="mt-1 text-xs text-slate-500">Bir neçə dəqiqə çəkə bilər. Sonda proqram yenidən başlayır, ekran qısa müddət cavab verməyə bilər.</p>}
          </div>
        </div>
      )}

      {/* ── 1. Mənbə ── */}
      <section className={`${CARD} p-5 sm:p-6`}>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Serverdəki backup-lardan bərpa</p>
            <h3 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">1 · Mənbəni seç</h3>
            <p className="mt-0.5 text-sm text-slate-500">Gecə (və «İndi al») backup-ları bütün tenantları əhatə edir. Onlardan tək bir tenantı və ya bütün bazanı bərpa etmək olar.</p>
          </div>
          <button type="button" onClick={loadSources} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"><RefreshCw className="h-3.5 w-3.5" /> Yenilə</button>
        </div>
        <div className="mb-3 inline-flex rounded-xl border border-slate-200 bg-white p-1">
          {[['night', 'Gecə backup-ları', Moon], ['upload', 'Kənardan fayl', Upload]].map(([k, l, Ic]) => (
            <button key={k} type="button" onClick={() => setSrcTab(k)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${srcTab === k ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><Ic className="h-3.5 w-3.5" />{l}</button>
          ))}
        </div>

        {srcTab === 'night' && (
          !sources ? <p className="flex items-center gap-2 py-6 text-sm text-slate-400"><LoaderCircle className="h-4 w-4 animate-spin" /> Yüklənir</p>
            : !sources.night.length ? <p className="py-6 text-sm text-slate-400">Serverdə gecə backup-ı yoxdur</p>
              : (
                <div className="grid max-h-72 gap-2 overflow-y-auto pr-1 sm:grid-cols-2 xl:grid-cols-3">
                  {sources.night.map(n => {
                    const on = src && src.kind === 'night' && src.name === n.ts;
                    return (
                      <button key={n.ts} type="button" onClick={() => pickSource({ kind: 'night', name: n.ts, label: `Gecə backup-ı ${n.date}` })}
                        className={`flex items-center gap-3 rounded-xl p-3 text-left transition-all ${on ? 'border-2 border-[#0090bb] bg-sky-50' : 'border border-slate-200 bg-white hover:border-slate-300'}`}>
                        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${on ? 'bg-[#0090bb] text-white' : 'bg-indigo-50 text-indigo-600'}`}><Moon className="h-4 w-4" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-slate-800">{n.date}</span>
                          <span className="block text-[11px] text-slate-500">DB {fmtSize(n.db?.size)}{n.img ? ` · şəkillər ${fmtSize(n.img.size)}` : ' · şəkil yoxdur'}</span>
                        </span>
                        {on && <Check className="h-4 w-4 text-[#0090bb]" />}
                      </button>
                    );
                  })}
                </div>
              )
        )}

        {srcTab === 'upload' && (
          <div className="space-y-4">
            <p className="flex gap-2 rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
              <Info className="h-4 w-4 flex-shrink-0 text-slate-400" />
              <span>Server itəndə və ya başqa yerdən gələn backup üçün: <b>fiscalpos_*.sql.gz</b> (baza) və istəyə görə <b>fiscalpos_uploads_*.tar.gz</b> (şəkillər). Böyük fayl hissə-hissə yüklənir. Google Drive-dakı <b>.gpg</b> nüsxələri əvvəlcə kompüterdə şifrədən çıxarılmalıdır.</span>
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {[['sql', 'Baza (.sql.gz / .sql)', '.sql,.gz', upSql, setUpSql, sqlRef, Database], ['img', 'Şəkillər (.tar.gz), istəyə görə', '.gz,.tgz', upImg, setUpImg, imgRef, ImageIcon]].map(([k, l, acc, f, setF, ref, Ic]) => (
                <div key={k} onClick={() => ref.current?.click()}
                  className="flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-4 hover:border-slate-300">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-slate-500 ring-1 ring-slate-200"><Ic className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-700">{l}</span>
                    <span className="block truncate text-xs text-slate-400">{f ? `${f.name} · ${fmtSize(f.size)}` : 'Seç'}</span>
                  </span>
                  <input ref={ref} type="file" accept={acc} className="hidden" onChange={e => { setF(e.target.files?.[0] || null); e.target.value = ''; }} />
                </div>
              ))}
            </div>
            {upPct && (
              <div>
                <div className="mb-1 flex justify-between text-xs font-medium text-slate-500">
                  <span>{upPct.label}: {upPct.checking ? 'serverdə yoxlanılır' : 'yüklənir'}</span><span className="font-mono">{upPct.pct}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-[#0090bb] transition-all" style={{ width: `${upPct.pct}%` }} />
                </div>
              </div>
            )}
            <button type="button" onClick={doUpload} disabled={(!upSql && !upImg) || upBusy}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40">
              {upBusy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {upBusy ? 'Yüklənir' : 'Serverə yüklə'}
            </button>
            {sources && sources.uploads.some(u => /\.sql(\.gz)?$/i.test(u.name)) && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-slate-500">Yüklənmiş fayllar</p>
                {sources.uploads.filter(u => /\.sql(\.gz)?$/i.test(u.name)).map(u => {
                  const on = src && src.kind === 'upload' && src.name === u.name;
                  const imgs = sources.uploads.filter(x => /\.(tar\.gz|tgz)$/i.test(x.name));
                  return (
                    <div key={u.name} className={`flex flex-wrap items-center gap-2 rounded-xl p-2.5 ${on ? 'bg-sky-50 ring-2 ring-[#0090bb]' : 'bg-white ring-1 ring-slate-200'}`}>
                      <Database className="h-4 w-4 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-700">{u.name} · {fmtSize(u.size)}</span>
                      {imgs.length > 0 && (
                        <select defaultValue="" id={`img-${u.name}`} className="rounded-lg border border-slate-200 px-2 py-1 text-xs">
                          <option value="">Şəkilsiz</option>
                          {imgs.map(i => <option key={i.name} value={i.name}>{i.name}</option>)}
                        </select>
                      )}
                      <button type="button" onClick={() => pickSource({ kind: 'upload', name: u.name, img: document.getElementById(`img-${u.name}`)?.value || undefined, label: u.name })}
                        className="rounded-lg bg-[#0090bb] px-3 py-1.5 text-xs font-semibold text-white">Seç</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── 2. Nə bərpa olunsun ── */}
      {src && (
        <section className={`${CARD} p-5 sm:p-6`}>
          <h3 className="text-lg font-semibold tracking-tight text-slate-900">2 · Nəyi bərpa edək?</h3>
          <p className="mt-0.5 text-sm text-slate-500">Mənbə: <b>{src.label}</b>{info ? ` · ${info.tenants.length} tenant` : ''}{info && !info.hasImages ? ' · şəkil arxivi yoxdur' : ''}</p>
          {inspBusy && !info && <p className="mt-4 flex items-center gap-2 text-sm text-slate-400"><LoaderCircle className="h-4 w-4 animate-spin" /> Backup oxunur</p>}

          {info && (
            <>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {[
                  ['tenant', 'Tək tenant', 'Yalnız seçdiyin restoran backup gününə qayıdır. Digər tenantlara toxunulmur.', Store, 'sky'],
                  ['full', 'Bütün baza', 'BÜTÜN restoranlar backup gününə qayıdır. Ondan sonrakı bütün dəyişikliklər itir.', HardDrive, 'red'],
                ].map(([k, t, d, Ic, tone]) => {
                  const on = mode === k;
                  return (
                    <button key={k} type="button" onClick={() => { setMode(k); setConfirm(''); setDone(null); }}
                      className={`flex items-start gap-3 rounded-2xl p-4 text-left transition-all ${on ? (tone === 'red' ? 'border-2 border-red-400 bg-red-50/60' : 'border-2 border-[#0090bb] bg-sky-50') : 'border border-slate-200 bg-white hover:border-slate-300'}`}>
                      <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${on ? (tone === 'red' ? 'bg-red-500 text-white' : 'bg-[#0090bb] text-white') : 'bg-slate-100 text-slate-500'}`}><Ic className="h-4 w-4" /></span>
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{t}</span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{d}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {info.hasImages && (
                <label className="mt-4 flex cursor-pointer items-center gap-2.5 text-sm text-slate-700">
                  <input type="checkbox" className="sa-switch" checked={withImg} onChange={e => setWithImg(e.target.checked)} />
                  Şəkilləri də bərpa et <span className="text-xs text-slate-400">(mövcud fayllar silinmir, yalnız əlavə olunur və ya üzərinə yazılır)</span>
                </label>
              )}

              {/* Tək tenant */}
              {mode === 'tenant' && (
                <div className="mt-5 grid gap-5 lg:grid-cols-[300px_1fr]">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-slate-600">Backup-dakı tenant</label>
                    <SearchSelect value={tid} onChange={pickTenant} placeholder="Tenant seçin"
                      options={info.tenants.map(t => ({ value: String(t.id), label: `${t.name || t.username} (#${t.id})${t.exists ? '' : ' · indi yoxdur'}` }))} />
                  </div>
                  <div>
                    {inspBusy && tid && <p className="flex items-center gap-2 text-sm text-slate-400"><LoaderCircle className="h-4 w-4 animate-spin" /> Hesablanır</p>}
                    {detail && !inspBusy && (
                      <>
                        {!detail.current ? (
                          <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">Bu tenant indi sistemdə yoxdur, bu yolla bərpa edilə bilməz.</p>
                        ) : (
                          <>
                            <div className="overflow-hidden rounded-xl ring-1 ring-slate-200">
                              <table className="w-full text-xs">
                                <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-400"><th className="px-3 py-2">Cədvəl</th><th className="px-3 py-2 text-right">İndi</th><th className="px-3 py-2 text-right">Backup-da</th></tr></thead>
                                <tbody className="divide-y divide-slate-100">
                                  {Object.entries(detail.counts).filter(([t, n]) => n || detail.liveCounts[t]).sort((a, b) => b[1] - a[1]).slice(0, 14).map(([t, n]) => {
                                    const live = detail.liveCounts[t];
                                    return (
                                      <tr key={t}><td className="px-3 py-1.5 font-mono text-slate-600">{t}</td>
                                        <td className="px-3 py-1.5 text-right text-slate-500">{live ?? '-'}</td>
                                        <td className={`px-3 py-1.5 text-right font-semibold ${live !== undefined && live !== n ? 'text-amber-700' : 'text-slate-800'}`}>{n}</td></tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-4 rounded-2xl bg-amber-50/70 p-4 ring-1 ring-amber-200/70">
                              <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><TriangleAlert className="h-4 w-4" /> {curName} backup gününə qaytarılacaq</p>
                              <p className="mt-1 text-xs text-amber-800/90">Bu restoranın backup-dan sonrakı sifariş, kassa, alış və dəyişiklikləri itəcək. Bərpadan əvvəl hazırkı vəziyyət avtomatik nüsxələnir. Təsdiq üçün adı yazın: <b className="font-mono">{curName}</b></p>
                              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Restoran adı"
                                  className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100" />
                                <button type="button" onClick={doTenantRestore} disabled={!nameOk || busy}
                                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-40">
                                  {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} {busy ? 'Bərpa olunur' : 'Bu tenantı bərpa et'}
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Bütün baza */}
              {mode === 'full' && (
                <div className="mt-5 rounded-2xl bg-red-50/70 p-4 ring-1 ring-red-200/70 sm:p-5">
                  <p className="flex items-center gap-2 text-sm font-semibold text-red-800"><TriangleAlert className="h-4 w-4" /> Bütün restoranlar backup gününə qayıdacaq</p>
                  <ul className="mt-2 space-y-1 text-xs leading-relaxed text-red-800/90">
                    <li>· Bütün tenantların backup-dan sonrakı bütün datası itəcək.</li>
                    <li>· Başlamazdan əvvəl hazırkı baza tam nüsxələnir (alınmasa bərpa edilmir); xəta olsa avtomatik geri qaytarılır.</li>
                    <li>· Sonda proqram yenidən başlayır; bərpa zamanı restoranlarda işləməsinlər.</li>
                  </ul>
                  <p className="mt-3 text-xs text-red-800">Təsdiq üçün yazın: <b className="font-mono">{FULL_CONFIRM}</b></p>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={FULL_CONFIRM}
                      className="min-w-0 flex-1 rounded-xl border border-red-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-red-100" />
                    <button type="button" onClick={doFullRestore} disabled={confirm.trim() !== FULL_CONFIRM || busy || fullRunning}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40">
                      {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <HardDrive className="h-4 w-4" />} Bütün bazanı bərpa et
                    </button>
                  </div>
                </div>
              )}

              {done && (
                <div className="mt-5 flex gap-3 rounded-2xl bg-emerald-50/70 p-4 ring-1 ring-emerald-200/60">
                  <CircleCheckBig className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                  <div className="min-w-0 text-sm">
                    <p className="font-semibold text-emerald-900">Bərpa tamamlandı</p>
                    <p className="text-xs text-emerald-800/90">Tenant #{done.tenant_id}: {done.tables} cədvəl, {done.rows} sətir, {done.images} şəkil.</p>
                    <p className="mt-1 truncate text-xs text-emerald-800/80">Bərpadan əvvəlki vəziyyət: <span className="font-mono">{done.safety}</span></p>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* ── Təhlükəsizlik nüsxələri ── */}
      <section className={`${CARD} p-5 sm:p-6`}>
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><ShieldCheck className="h-4 w-4" /></span>
          <div>
            <h3 className="text-[15px] font-semibold text-slate-900">Təhlükəsizlik nüsxələri</h3>
            <p className="text-xs text-slate-500">Hər təmizləmə və bərpadan ƏVVƏL avtomatik alınır, 60 gün saxlanır. Səhv olsa buradan geri qaytar.</p>
          </div>
        </div>
        {!sources ? null : !sources.safety.length ? (
          <p className="mt-4 text-sm text-slate-400">Hələ nüsxə yoxdur</p>
        ) : (
          <div className="mt-4 max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-xl ring-1 ring-slate-200">
            {sources.safety.map(f => {
              const t = tenantOfFile(f.name);
              const isZip = /\.zip$/i.test(f.name);
              return (
                <div key={f.name} className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800">{isZip ? (t ? t.name : f.name) : 'Bütün baza (bərpadan əvvəl)'}</span>
                    <span className="block truncate font-mono text-[11px] text-slate-400">{f.name} · {fmtSize(f.size)} · {fmtTime(f.mtime)}</span>
                  </span>
                  <button type="button" onClick={() => safetyDownload(f.name)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> Endir</button>
                  {isZip && t && (
                    <button type="button" onClick={() => { setSafeTarget({ file: f.name, tenant: t }); setSafeConfirm(''); }}
                      className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100"><RotateCcw className="h-3.5 w-3.5" /> Geri qaytar</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {safeTarget && (
          <div className="mt-4 rounded-2xl bg-amber-50/70 p-4 ring-1 ring-amber-200/70">
            <p className="text-sm font-semibold text-amber-900">{safeTarget.tenant.name} bu nüsxənin vəziyyətinə qaytarılsın?</p>
            <p className="mt-1 text-xs text-amber-800/90">Hazırkı vəziyyət də əvvəlcə nüsxələnir. Təsdiq üçün adı yazın: <b className="font-mono">{safeTarget.tenant.name}</b></p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input value={safeConfirm} onChange={e => setSafeConfirm(e.target.value)} placeholder="Restoran adı"
                className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100" />
              <button type="button" onClick={() => setSafeTarget(null)} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600">Ləğv et</button>
              <button type="button" onClick={safetyApply} disabled={safeConfirm.trim() !== String(safeTarget.tenant.name || '').trim() || busy}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-40">
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Geri qaytar
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
